<template>
  <div class="amj-app" :class="{ 'amj-frontend': isFrontend }">
    <!-- 前端模式：显示顶部导航 -->
    <template v-if="isFrontend">
      <div class="amj-header">
        <div class="header-left">
          <div class="amj-logo">
            <el-icon size="28" color="#409EFF"><VideoPlay /></el-icon>
            <span>AI漫剧生成器</span>
          </div>
          <el-menu
            :default-active="activeMenu"
            mode="horizontal"
            class="header-menu"
            @select="handleMenuSelect"
          >
            <el-menu-item index="dashboard">
              <el-icon><User /></el-icon>
              <span>个人中心</span>
            </el-menu-item>
            <el-menu-item index="workspace" :disabled="!projectStore.projectId">
              <el-icon><Edit /></el-icon>
              <span>项目编辑</span>
            </el-menu-item>
          </el-menu>
        </div>
        <div class="header-right">
          <el-dropdown>
            <span class="user-info">
              <el-icon><UserFilled /></el-icon>
              {{ userDisplayName }}
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item @click="goToDashboard">个人中心</el-dropdown-item>
                <el-dropdown-item divided @click="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
      
      <div class="amj-content">
        <router-view />
      </div>
    </template>
    
    <!-- 后台模式：显示侧边导航 -->
    <template v-else>
      <el-container class="amj-workspace">
        <!-- 左侧导航 -->
        <el-aside width="240px" class="amj-sidebar">
          <div class="amj-logo">
            <el-icon size="32" color="#409EFF"><VideoPlay /></el-icon>
            <span>AI漫剧生成器</span>
          </div>
          
          <el-menu
            :default-active="activeMenu"
            class="amj-menu"
            @select="handleMenuSelect"
          >
            <el-menu-item index="text">
              <el-icon><Document /></el-icon>
              <span>原文管理</span>
            </el-menu-item>
            
            <el-menu-item index="settings">
              <el-icon><User /></el-icon>
              <span>设定管理</span>
            </el-menu-item>
            
            <el-menu-item index="script">
              <el-icon><Reading /></el-icon>
              <span>剧本拆解</span>
            </el-menu-item>
            
            <el-menu-item index="voice">
              <el-icon><Microphone /></el-icon>
              <span>语音合成</span>
            </el-menu-item>
            
            <el-menu-item index="images">
              <el-icon><Picture /></el-icon>
              <span>图片生成</span>
            </el-menu-item>
            
            <el-menu-item index="videos">
              <el-icon><VideoCamera /></el-icon>
              <span>视频生成</span>
            </el-menu-item>
            
            <el-menu-item index="compose">
              <el-icon><Film /></el-icon>
              <span>视频合成</span>
            </el-menu-item>
          </el-menu>
          
          <!-- 项目进度 -->
          <div class="amj-progress">
            <h4>项目进度</h4>
            <el-steps direction="vertical" :active="projectStore.progressStep" size="small">
              <el-step title="上传文本" />
              <el-step title="提取设定" />
              <el-step title="生成剧本" />
              <el-step title="生成语音" />
              <el-step title="生成图片" />
              <el-step title="生成视频" />
              <el-step title="合成完成" />
            </el-steps>
          </div>
        </el-aside>
        
        <!-- 主内容区 -->
        <el-main class="amj-main">
          <router-view />
        </el-main>
      </el-container>
    </template>
  </div>
</template>

<script setup>
import { computed, inject, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useProjectStore } from './stores/project'
import { ElMessage } from 'element-plus'

const route = useRoute()
const router = useRouter()
const projectStore = useProjectStore()
const amjSettings = inject('amjSettings')

// 是否为前端模式
const isFrontend = computed(() => amjSettings.isFrontend)

// 用户显示名称
const userDisplayName = ref('用户')

// 当前激活的菜单
const activeMenu = computed(() => {
  if (isFrontend.value) {
    return route.name === 'workspace' ? 'workspace' : 'dashboard'
  }
  return route.name || 'text'
})

// 菜单选择处理
const handleMenuSelect = (index) => {
  if (index === 'dashboard') {
    router.push({ name: 'dashboard' })
  } else if (index === 'workspace') {
    if (projectStore.projectId) {
      router.push({ name: 'workspace', params: { projectId: projectStore.projectId } })
    } else {
      ElMessage.warning('请先选择或创建一个项目')
      router.push({ name: 'dashboard' })
    }
  } else {
    router.push({ name: index })
  }
}

// 返回个人中心
const goToDashboard = () => {
  router.push({ name: 'dashboard' })
}

// 退出登录
const logout = () => {
  // 跳转到WordPress登录页面
  window.location.href = '/wp-login.php?action=logout'
}

// 初始化
onMounted(async () => {
  // 设置用户信息
  if (amjSettings.userId) {
    userDisplayName.value = `用户${amjSettings.userId}`
  }

  // 前端模式下，根据初始视图进行导航
  if (isFrontend.value && amjSettings.initialView) {
    const initialView = amjSettings.initialView
    if (initialView === 'workspace' && amjSettings.projectId) {
      router.push({ 
        name: 'workspace', 
        params: { projectId: amjSettings.projectId } 
      })
    } else {
      router.push({ name: 'dashboard' })
    }
  }

  // 加载项目
  if (amjSettings.projectId) {
    projectStore.setProjectId(amjSettings.projectId)
    try {
      await projectStore.fetchProject()
    } catch (error) {
      console.error('Failed to load project:', error)
      ElMessage.error('加载项目失败')
    }
  }
})
</script>

<style scoped lang="scss">
.amj-app {
  height: 100vh;
}

// 前端模式样式
.amj-frontend {
  display: flex;
  flex-direction: column;
  background: #f5f7fa;

  .amj-header {
    height: 60px;
    background: #fff;
    border-bottom: 1px solid #e4e7ed;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 24px;

    .header-left {
      display: flex;
      align-items: center;
      gap: 40px;

      .amj-logo {
        display: flex;
        align-items: center;
        gap: 10px;

        span {
          font-size: 18px;
          font-weight: 600;
          color: #303133;
        }
      }

      .header-menu {
        border-bottom: none;
      }
    }

    .header-right {
      .user-info {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        color: #606266;
        font-size: 14px;

        &:hover {
          color: #409eff;
        }
      }
    }
  }

  .amj-content {
    flex: 1;
    overflow: auto;
    width: 100%;
  }
}

// 后台模式样式
.amj-workspace {
  height: 100%;
  background: #f5f7fa;
}

.amj-sidebar {
  background: #fff;
  border-right: 1px solid #e4e7ed;
  display: flex;
  flex-direction: column;
}

.amj-logo {
  height: 60px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  border-bottom: 1px solid #e4e7ed;

  .el-icon {
    margin-right: 12px;
  }

  span {
    font-size: 18px;
    font-weight: 600;
    color: #303133;
  }
}

.amj-menu {
  border-right: none;
  flex: 1;
}

.amj-progress {
  padding: 20px;
  border-top: 1px solid #e4e7ed;

  h4 {
    margin: 0 0 16px;
    font-size: 14px;
    color: #606266;
  }
}

.amj-main {
  padding: 20px;
  overflow-y: auto;
}
</style>
